import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import StatCard from '../components/StatCard';
import DateRangePicker from '../components/DateRangePicker';
import StatusBadge from '../components/StatusBadge';
import { formatDuration, formatDateTime } from '../utils/formatters';
import { useDriverStats } from '../hooks/useDriverStats';

export default function DriverStats() {
  const {
    dateRange, setDateRange,
    drivers, loading, error,
    selectedDriver, setSelectedDriver,
    detailDeliveries, detailLoading,
    openDetail,
  } = useDriverStats();

  if (selectedDriver) {
    return (
        <div className="space-y-6">
          <button
              onClick={() => setSelectedDriver(null)}
              className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to all drivers
          </button>

          <header>
            <h1 className="text-2xl font-semibold text-stone-900">{selectedDriver.driver_name}</h1>
            <p className="text-sm text-stone-500 mt-1">
              {dateRange?.from?.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
              {dateRange?.to && dateRange.to !== dateRange.from ? ` — ${dateRange.to.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
            </p>
          </header>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Deliveries" value={selectedDriver.total} />
            <StatCard label="Completed" value={selectedDriver.completed} accent="emerald" />
            <StatCard label="Failed" value={selectedDriver.failed} accent="red" />
            <StatCard label="Avg Driving Time" value={formatDuration(selectedDriver.avg_driving_time_seconds)} />
          </div>

          <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-stone-200">
              <h2 className="font-semibold text-stone-900">Delivery History</h2>
            </div>
            {detailLoading ? (
                <div className="px-4 py-10 text-center text-sm text-stone-500">Loading…</div>
            ) : detailDeliveries.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-stone-500">No deliveries in this range.</div>
            ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Response Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Driving Time</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                    {detailDeliveries.map((d) => (
                        <tr key={d.delivery_id} className="hover:bg-stone-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-stone-900">{d.order_reference}</td>
                          <td className="px-4 py-3">
                            <StatusBadge status={d.status} />
                          </td>
                          <td className="px-4 py-3 text-stone-600 text-xs">{formatDateTime(d.created_at)}</td>
                          <td className="px-4 py-3 text-stone-600">{formatDuration(d.response_time_seconds)}</td>
                          <td className="px-4 py-3 text-stone-600">{formatDuration(d.driving_time_seconds)}</td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
            )}
          </section>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-stone-900">Driver Stats</h1>
          <p className="text-sm text-stone-500 mt-1">Delivery volume, outcomes, and timing across all drivers.</p>
        </header>

        <div className="flex items-center gap-3">
          <DateRangePicker align="left" value={dateRange} onChange={setDateRange} />
        </div>

        {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
        )}

        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900">All Drivers</h2>
            <span className="text-sm text-stone-500">{drivers.length} total</span>
          </div>

          {loading ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">Loading…</div>
          ) : drivers.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">No delivery activity in this range.</div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Driver</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Completed</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Failed</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">On Route</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Avg Response</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Avg Driving Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Avg Full Time</th>
                    <th className="px-4 py-3" />
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {drivers.map((d) => (
                      <tr key={d.driver_id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-stone-900">{d.driver_name}</td>
                        <td className="px-4 py-3 text-stone-600">{d.total}</td>
                        <td className="px-4 py-3 text-emerald-700 font-medium">{d.completed}</td>
                        <td className="px-4 py-3 text-red-600 font-medium">{d.failed}</td>
                        <td className="px-4 py-3 text-cyan-700">{d.on_route}</td>
                        <td className="px-4 py-3 text-stone-600">{formatDuration(d.avg_response_time_seconds)}</td>
                        <td className="px-4 py-3 text-stone-600">{formatDuration(d.avg_driving_time_seconds)}</td>
                        <td className="px-4 py-3 text-stone-600">{formatDuration(d.avg_full_lifecycle_seconds)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                              onClick={() => openDetail(d)}
                              className="rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-[#ee2b74] hover:bg-[#d4245f] transition"
                          >
                            View Detail
                          </button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>
      </div>
  );
}